import { useQuery } from '@tanstack/react-query';
import axios from '../common/api/request';

// ── Query keys ───────────────────────────────────────────────────────────────

export const effectiveOperatorsKeys = {
  /** Root key — invalidating this clears all effective-operator queries. */
  all: ['effective-operators'] as const,
  /** Scoped by year and module. */
  list: (params: { year?: number; module?: string }) =>
    [...effectiveOperatorsKeys.all, params] as const,
};

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Fetch the **effective** list of counterpart operator PPO names the user is
 * allowed to see — computed as `Settings ∩ Preferences`.
 *
 * Endpoint: `GET /user-preferences/effective-operators?year={year}&module={module}`
 *
 * - When the user has preferences → returns only the intersection.
 * - When the user has NO preferences → returns ALL Settings operators (full scope).
 *
 * This is the **primary hook** for populating operator dropdowns across all
 * module pages, replacing the previous Settings-based operator lists.
 *
 * @param year    Optional accounting year to scope operators.
 * @param module  Optional module name (`eno1`, `e53`, `e55`, `e09`, `tdacc`).
 * @param enabled When `false` the query is disabled (no request is made).
 */
export function useEffectiveOperators(
  year?: number,
  module?: string,
  enabled = true,
) {
  const { data, isLoading, error, refetch } = useQuery<string[]>({
    queryKey: effectiveOperatorsKeys.list({ year, module }),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (year != null) params.append('year', String(year));
      if (module) params.append('module', module);

      const resp = await axios.get<string[]>(
        `/user-preferences/effective-operators?${params.toString()}`,
      );
      return resp.data;
    },
    enabled,
  });

  return {
    /** Sorted list of effective PPO name strings (e.g. `["ATA","DPA","FRA"]`). */
    operators: data ?? [],
    isLoading,
    error,
    refetch,
  };
}

